/**
 * Landing Page Service
 * CRUD operations for lead capture landing pages
 */

import { supabase } from "./supabase";
import { uploadFile, getPublicUrl } from "./storage.service";
import type { UUID } from "../types/common.types";
import type {
  LandingPage,
  CreateLandingPageInput,
  UpdateLandingPageInput,
} from "../types/landingPage.types";

const BUCKET = "landing-page-images";

// =====================================================
// CRUD
// =====================================================

export async function getLandingPages(): Promise<LandingPage[]> {
  const { data, error } = await supabase
    .from("landing_pages")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }
  return (data || []) as LandingPage[];
}

export async function getLandingPage(id: UUID): Promise<LandingPage | null> {
  const { data, error } = await supabase
    .from("landing_pages")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }
  return data as LandingPage | null;
}

export async function getLandingPageBySlug(
  slug: string,
): Promise<LandingPage | null> {
  const { data, error } = await supabase
    .from("landing_pages")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();

  if (error) {
    throw error;
  }
  return data as LandingPage | null;
}

export async function createLandingPage(
  input: CreateLandingPageInput,
  userId?: UUID,
): Promise<LandingPage> {
  const { data, error } = await supabase
    .from("landing_pages")
    .insert({ ...input, created_by: userId, updated_by: userId })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as LandingPage;
}

export async function updateLandingPage(
  id: UUID,
  updates: UpdateLandingPageInput,
  userId?: UUID,
): Promise<LandingPage> {
  const { data, error } = await supabase
    .from("landing_pages")
    .update({ ...updates, updated_by: userId })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as LandingPage;
}

export async function deleteLandingPage(id: UUID): Promise<void> {
  const { error } = await supabase.from("landing_pages").delete().eq("id", id);

  if (error) {
    throw error;
  }
}

// =====================================================
// SLUG UTILITIES
// =====================================================

export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export async function isSlugAvailable(
  slug: string,
  excludeId?: UUID,
): Promise<boolean> {
  let query = supabase
    .from("landing_pages")
    .select("id", { count: "exact", head: true })
    .eq("slug", slug);

  if (excludeId) {
    query = query.neq("id", excludeId);
  }

  const { count, error } = await query;
  if (error) {
    throw error;
  }
  return (count || 0) === 0;
}

// =====================================================
// IMAGE UPLOAD
// =====================================================

export async function uploadLandingPageImage(file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const path = `${crypto.randomUUID()}.${ext}`;

  await uploadFile({
    provider: "supabase",
    bucket: BUCKET,
    path,
    file,
    contentType: file.type,
    upsert: true,
  });

  return getPublicUrl("supabase", BUCKET, path);
}

export async function uploadLandingPageContent(file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "pdf";
  const path = `content/${crypto.randomUUID()}.${ext}`;

  await uploadFile({
    provider: "supabase",
    bucket: BUCKET,
    path,
    file,
    contentType: file.type,
    upsert: true,
  });

  return getPublicUrl("supabase", BUCKET, path);
}
